import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, Shield, Globe, ChevronDown, Clock, Phone } from 'lucide-react';
import toast from 'react-hot-toast';
import OtpVerification from '../auth/OtpVerification';
import ChangePassword from '../auth/ChangePassword';
import authService from '../../api/authService';
import { navigateToRoleDashboard } from '../../utils/navigation';
import MessagingService from '../../services/MessagingServices';
import { registrationService } from '../../api/registrationService';
import { getOrganizationByDomain } from '../../api/tenantService';
import logo from '../../assets/images/logo.jpg';
import { FcGoogle } from "react-icons/fc";
import { FaLinkedin, FaFacebook } from "react-icons/fa";
import {
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  CheckCircle2,
  ChevronRight,
  Headphones,
  HelpCircle,
  Menu,
  Search,
  Sparkles,
  TrendingUp,
  Users,
  UserCheck,
  Wifi,
  X
} from 'lucide-react';
import LandingChatbot from '../../components/chatbot/LandingChatbot';
import ActiveSessionLoginPrompt from '../../components/session/ActiveSessionLoginPrompt';
import { Modal } from '../../components/ui/Modal';
import KnowledgeSearch from '../portal/KnowledgeSearch';
import { articleService, extractRegistrationListData } from '../../api/articleService';
import DefaultPasswordRequirementsPopover from '../../components/auth/DefaultPasswordRequirementsPopover';
import { isDefaultPasswordValid } from '../../utils/passwordValidation';

const navLinks = [
  { href: '#login-section', label: 'Home' },
  { href: '#quick-stats', label: 'Quick Stats' },
  { href: '#features', label: 'Features' },
  { href: '#knowledge', label: 'Knowledge Base' },
  { href: '#contact', label: 'Contact Support' }
];

const integrations = ['Jira', 'Slack', 'Teams', 'Salesforce'];

const featureHighlights = [
  {
    icon: <Headphones className="w-5 h-5 text-[#006600]" />,
    title: 'Connected Workflows',
    copy: 'Guide every interaction with AI prompts and automations.'
  },
  {
    icon: <BookOpen className="w-5 h-5 text-[#006600]" />,
    title: 'Unified Knowledge',
    copy: 'Rich articles, media, and snippets across teams and channels.'
  },
  {
    icon: <Sparkles className="w-5 h-5 text-[#006600]" />,
    title: 'Predictive Routing',
    copy: 'Surface best-fit agents and automatically escalate urgent work.'
  }
];

const heroRequests = [
  { id: 'TKT-10950', title: 'DB Connection Failure', status: 'In Progress', accent: 'text-amber-600' },
  { id: 'TKT-10948', title: 'License Upgrade', status: 'Pending', accent: 'text-blue-600' }
];

/** Target titles for featured KB (loaded from API; fallback to these strings if fetch fails). */
const FEATURED_KB_TARGET_TITLES = [
  'How to Create a Support Ticket',
  'How to Change or Reset Your Account Password'
];

/** Same list as featured targets; legacy name so any remaining references do not throw. */
const knowledgeResults = FEATURED_KB_TARGET_TITLES;

function formatKbArticleDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return null;
  }
}

function pickFeaturedArticleRow(rows, exactTitle) {
  const norm = (s) => (s || '').trim().toLowerCase();
  const want = norm(exactTitle);
  const exact = rows.find((a) => norm(a.title) === want || norm(a.question) === want);
  if (exact) return exact;
  const prefix = want.slice(0, Math.min(24, want.length));
  return (
    rows.find((a) => norm(a.title).includes(prefix) || norm(a.question || '').includes(prefix)) ||
    rows[0]
  );
}

const supportPillars = [
  {
    icon: <Wifi className="w-5 h-5 text-[#006600]" />,
    title: 'Always-on Portal',
    copy: 'Give employees a single place to open, track, and resolve requests.'
  },
  {
    icon: <HelpCircle className="w-5 h-5 text-[#006600]" />,
    title: 'Guided Help Journeys',
    copy: 'Trigger proactive nudges and interactive troubleshooting trees.'
  },
  {
    icon: <CheckCircle2 className="w-5 h-5 text-[#006600]" />,
    title: 'Quality Insights',
    copy: 'Spot gaps with CSAT trends, backlog risk, and time-to-resolution.'
  }
];

/**
 * Helper function to extract user-friendly error message from various error formats
 * Handles: JSON strings, ResponseBean objects, error objects, plain strings, connection errors
 * Priority: error.message (may contain JSON string) > error.response.data > fallback
 */
const extractErrorMessage = (error) => {
  if (!error) return 'An error occurred';

  const code = error?.message;
  if (code === 'LOGIN_POLL_TIMEOUT') {
    return 'Sign-in approval timed out. The signed-in user must approve within the time limit, or try again.';
  }
  if (code === 'LOGIN_REJECTED') {
    return 'This sign-in was rejected from your other session.';
  }
  if (code === 'LOGIN_PENDING_FLOW_FAILED' || code === 'SESSION_FLOW_TERMINATED') {
    return 'Could not complete sign-in approval. Please try again.';
  }
  
  // Check for connection/network errors first
  const errorString = String(error?.message || error || '');
  if (errorString.includes('Connection refused') ||
      errorString.includes('ECONNREFUSED') ||
      errorString.includes('getsockopt') ||
      errorString.includes('Service is temporarily unavailable') ||
      errorString.includes('Failed to fetch') ||
      errorString.includes('NetworkError')) {
    return 'Service is temporarily unavailable. Please try again later.';
  }
  
  // Priority 1: Handle error.message (apiClient throws errors with JSON string in message)
  if (error?.message) {
    const message = error.message;
    
    // If message looks like JSON (starts with { or [), try to parse it
    if (typeof message === 'string' && (message.trim().startsWith('{') || message.trim().startsWith('['))) {
      try {
        const parsed = JSON.parse(message);
        // Check for connection errors in parsed JSON
        if (parsed.message?.includes('Connection refused') || 
            parsed.error?.includes('Connection refused') ||
            parsed.message?.includes('Connection')) {
          return 'Service is temporarily unavailable. Please try again later.';
        }
        // Extract message from parsed JSON
        if (parsed.message) return parsed.message;
        if (parsed.data?.message) return parsed.data.message;
        // If no message field, return a formatted version
        if (parsed.status) {
          return parsed.message || `Error: ${parsed.status}`;
        }
      } catch (e) {
        // Not valid JSON, continue to other checks
      }
    }
    
    // If message is not JSON or parsing failed, return it if it's reasonable
    // Exclude technical error details (stack traces, etc.)
    if (message.length < 500 && 
        !message.includes('{') && 
        !message.includes('status') &&
        !message.includes('trace') &&
        !message.includes('stack') &&
        !message.includes('at ') &&
        !message.includes('Exception')) {
      return message;
    }
  }
  
  // Priority 2: Handle error.response.data (axios/fetch error structure)
  if (error?.response?.data) {
    const responseData = error.response.data;
    
    // If responseData is a string, try to parse it as JSON
    if (typeof responseData === 'string') {
      // Check for connection errors
      if (responseData.includes('Connection refused') || 
          responseData.includes('getsockopt')) {
        return 'Service is temporarily unavailable. Please try again later.';
      }
      try {
        const parsed = JSON.parse(responseData);
        if (parsed.message?.includes('Connection refused') || 
            parsed.error?.includes('Connection refused')) {
          return 'Service is temporarily unavailable. Please try again later.';
        }
        if (parsed.message) return parsed.message;
        if (parsed.data?.message) return parsed.data.message;
      } catch (e) {
        // Not JSON, return as-is if reasonable length and not technical
        if (responseData.length < 500 && 
            !responseData.includes('trace') &&
            !responseData.includes('stack')) {
          return responseData;
        }
      }
    }
    
    // If responseData is an object
    if (typeof responseData === 'object' && responseData !== null) {
      // Check for connection errors
      if (responseData.message?.includes('Connection refused') || 
          responseData.error?.includes('Connection refused')) {
        return 'Service is temporarily unavailable. Please try again later.';
      }
      if (responseData.message) return responseData.message;
      if (responseData.data?.message) return responseData.data.message;
    }
  }
  
  // Priority 3: If error is a string directly
  if (typeof error === 'string') {
    // Check for connection errors
    if (error.includes('Connection refused') || 
        error.includes('getsockopt')) {
      return 'Service is temporarily unavailable. Please try again later.';
    }
    // Try to parse as JSON
    if (error.trim().startsWith('{') || error.trim().startsWith('[')) {
      try {
        const parsed = JSON.parse(error);
        if (parsed.message?.includes('Connection refused') || 
            parsed.error?.includes('Connection refused')) {
          return 'Service is temporarily unavailable. Please try again later.';
        }
        if (parsed.message) return parsed.message;
        if (parsed.data?.message) return parsed.data.message;
      } catch (e) {
        // Not JSON
      }
    }
    // Return as-is if reasonable and not technical
    if (error.length < 500 && 
        !error.includes('trace') &&
        !error.includes('stack')) {
      return error;
    }
  }
  
  // Fallback
  return 'An unexpected error occurred. Please try again.';
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showSearchKBModal, setShowSearchKBModal] = useState(false);
  const [kbModalSeedQuery, setKbModalSeedQuery] = useState('');
  const [showContactBar, setShowContactBar] = useState(false);

  /** Clear KB modal seed after close animation so the search UI does not reset while the modal is still visible. */
  useEffect(() => {
    if (showSearchKBModal) return undefined;
    const t = setTimeout(() => setKbModalSeedQuery(''), 320);
    return () => clearTimeout(t);
  }, [showSearchKBModal]);

  // Signin state management
  const [step, setStep] = useState('email'); // 'email' | 'otp' | 'password' | 'change-password' | 'login-choice'
  const [loginEmail, setLoginEmail] = useState('');
  const [password, setPassword] = useState('');
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
  const [verifiedOtp, setVerifiedOtp] = useState(''); // Store verified OTP for password change
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [language, setLanguage] = useState('English');
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpExpiresAt, setOtpExpiresAt] = useState(null); // Store OTP expiration time

  // Customer signup modal state
  const [showCustomerSignupModal, setShowCustomerSignupModal] = useState(false);
  const [signupFormData, setSignupFormData] = useState({
    fullName: '',
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    phone: '',
    defaultPassword: '',
    role: 'CUSTOMER'
  });
  const [signupLoading, setSignupLoading] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [signupError, setSignupError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({
    email: '',
    phone: ''
  });
  /** Stops Chrome/password managers from injecting email until the user focuses the field */
  const [signupEmailReadOnly, setSignupEmailReadOnly] = useState(true);

  // Forgot password state
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);

  // Reset password state
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordEmail, setResetPasswordEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState('');
  const [showResetNewPassword, setShowResetNewPassword] = useState(false);
  const [showResetConfirmPassword, setShowResetConfirmPassword] = useState(false);
  const [resetTokenGeneratedAt, setResetTokenGeneratedAt] = useState(null); // Timestamp when token was generated
  const [resetTokenCountdown, setResetTokenCountdown] = useState(300); // 5 minutes in seconds

  // Organization data from domain lookup
  const [organizationData, setOrganizationData] = useState(null);
  const [organizationLoading, setOrganizationLoading] = useState(false);
  const [organizationError, setOrganizationError] = useState(null);
  const [googleClientId, setGoogleClientId] = useState('');
  const [showGoogleConsentModal, setShowGoogleConsentModal] = useState(false);
  const [pendingGoogleLogin, setPendingGoogleLogin] = useState(null);

  // State for dashboard metrics (read from sessionStorage)
  const [tenantName, setTenantName] = useState(() => {
    return sessionStorage.getItem('tenantName') || 'Helpdesk';
  });
  const [activeUserCount, setActiveUserCount] = useState(() => {
    return parseInt(sessionStorage.getItem('activeUserCount') || '0', 10);
  });
  const [ticketsResolvedAndClosed, setTicketsResolvedAndClosed] = useState(() => {
    return parseInt(sessionStorage.getItem('ticketsResolvedAndClosed') || '0', 10);
  });
  const [ticketsRaisedLast30Days, setTicketsRaisedLast30Days] = useState(() => {
    return parseInt(sessionStorage.getItem('ticketsRaisedLast30Days') || '0', 10);
  });

  /** Hero + Knowledge section: no-auth KB search (same rules as customer portal: active/published). */
  const [landingKbQuery, setLandingKbQuery] = useState('');
  const [landingKbHits, setLandingKbHits] = useState([]);
  const [landingKbLoading, setLandingKbLoading] = useState(false);
  const [landingKbError, setLandingKbError] = useState(null);
  const landingKbDebounceRef = useRef(null);

  /** Cancels PENDING_APPROVAL polling when the user leaves the page or starts a new login attempt. */
  const sessionConflictAbortRef = useRef(null);
  /** User B: modal state for active-session conflict (challenge ctx + resolver). */
  const [activeSessionPrompt, setActiveSessionPrompt] = useState(null);

  useEffect(() => {
    return () => {
      sessionConflictAbortRef.current?.abort();
    };
  }, []);

  const beginSessionConflictAttempt = useCallback(() => {
    sessionConflictAbortRef.current?.abort();
    const ac = new AbortController();
    sessionConflictAbortRef.current = ac;

    const waitForUserChoice = (ctx) =>
      new Promise((resolve, reject) => {
        if (ac.signal.aborted) {
          const err = new Error('LOGIN_POLL_ABORTED');
          err.name = 'AbortError';
          reject(err);
          return;
        }
        let settled = false;
        const finish = (fn, arg) => {
          if (settled) return;
          settled = true;
          ac.signal.removeEventListener('abort', onAbort);
          setActiveSessionPrompt(null);
          fn(arg);
        };
        const onAbort = () => {
          const err = new Error('LOGIN_POLL_ABORTED');
          err.name = 'AbortError';
          finish(reject, err);
        };
        ac.signal.addEventListener('abort', onAbort, { once: true });
        setActiveSessionPrompt({
          ...ctx,
          receivedAtMs: Date.now(),
          resolve: (v) => finish(resolve, v),
          reject: (e) => finish(reject, e),
        });
      });

    return { signal: ac.signal, waitForUserChoice };
  }, []);

  /** Featured articles (hero + #knowledge) from no-auth API — matches DB titles above. */
  const [featuredKbArticles, setFeaturedKbArticles] = useState([]);
  const [featuredKbLoading, setFeaturedKbLoading] = useState(false);

  const resolveLandingTenantId = useCallback(() => {
    if (organizationData?.tenantId != null && organizationData.tenantId !== '') {
      const n = Number(organizationData.tenantId);
      if (Number.isFinite(n)) return n;
    }
    const raw = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('tenantId') : null;
    if (raw) {
      const n = parseInt(raw, 10);
      if (Number.isFinite(n)) return n;
    }
    return undefined;
  }, [organizationData]);

  const runLandingKbSearch = useCallback(
    async (q) => {
      const term = (q || '').trim();
      if (!term) {
        setLandingKbHits([]);
        return;
      }
      setLandingKbLoading(true);
      setLandingKbError(null);
      try {
        const tid = resolveLandingTenantId();
        const res = await articleService.searchArticlesNoAuth(term, tid !== undefined ? { tenantId: tid } : {});
        let list = extractRegistrationListData(res);
        list = list.filter((a) => {
          const s = (a.status || '').toLowerCase();
          return s === 'active' || s === 'published';
        });
        setLandingKbHits(
          list.map((a) => ({
            id: a.articleId ?? a.id,
            title: a.title || a.question || 'Article'
          }))
        );
      } catch (e) {
        console.error('Landing KB search:', e);
        setLandingKbError('Could not search the knowledge base. Try again.');
        setLandingKbHits([]);
      } finally {
        setLandingKbLoading(false);
      }
    },
    [resolveLandingTenantId]
  );

  const loadFeaturedKbArticles = useCallback(async () => {
    setFeaturedKbLoading(true);
    const tid = resolveLandingTenantId();
    const opts = tid !== undefined ? { tenantId: tid } : {};
    const next = [];
    try {
      for (const targetTitle of FEATURED_KB_TARGET_TITLES) {
        try {
          const res = await articleService.searchArticlesNoAuth(
            targetTitle.length > 120 ? targetTitle.slice(0, 120) : targetTitle,
            opts
          );
          let list = extractRegistrationListData(res);
          list = list.filter((a) => {
            const s = (a.status || '').toLowerCase();
            return s === 'active' || s === 'published';
          });
          const picked = pickFeaturedArticleRow(list, targetTitle);
          if (picked) {
            next.push({
              id: picked.articleId ?? picked.id,
              title: picked.title || picked.question || targetTitle,
              updatedAt: picked.updatedAt || picked.createdAt || null
            });
          } else {
            next.push({
              id: `kb-missing-${next.length}`,
              title: targetTitle,
              updatedAt: null
            });
          }
        } catch (err) {
          console.warn('Featured KB article fetch failed:', targetTitle, err);
          next.push({
            id: `kb-err-${next.length}`,
            title: targetTitle,
            updatedAt: null
          });
        }
      }
      setFeaturedKbArticles(next);
    } finally {
      setFeaturedKbLoading(false);
    }
  }, [resolveLandingTenantId]);

  useEffect(() => {
    loadFeaturedKbArticles();
  }, [loadFeaturedKbArticles]);

  useEffect(() => {
    if (landingKbDebounceRef.current) {
      clearTimeout(landingKbDebounceRef.current);
    }
    const term = (landingKbQuery || '').trim();
    if (!term) {
      setLandingKbHits([]);
      setLandingKbError(null);
      setLandingKbLoading(false);
      return;
    }
    landingKbDebounceRef.current = setTimeout(() => {
      runLandingKbSearch(term);
    }, 400);
    return () => {
      if (landingKbDebounceRef.current) {
        clearTimeout(landingKbDebounceRef.current);
      }
    };
  }, [landingKbQuery, runLandingKbSearch]);

  /**
   * Opens Search Knowledge Base modal with optional seed query.
   * @param {string} query
   * @param {{ syncLandingHero?: boolean }} [options] - If syncLandingHero is false (e.g. #knowledge cards), do not set hero field so debounced search does not replace featured lists.
   */
  const openKbSearchModalWithQuery = useCallback((query, options = {}) => {
    const q = typeof query === 'string' ? query.trim() : '';
    const { syncLandingHero = true } = options;
    if (syncLandingHero) {
      setLandingKbQuery(q);
    }
    setKbModalSeedQuery(q);
    setShowSearchKBModal(true);
  }, []);

  const openHeroKnowledgeSearchModal = useCallback(() => {
    openKbSearchModalWithQuery(landingKbQuery || '');
  }, [landingKbQuery, openKbSearchModalWithQuery]);

  // Update dashboard metrics from sessionStorage when organization data is loaded
  useEffect(() => {
    const updateMetricsFromStorage = () => {
      const storedTenantName = sessionStorage.getItem('tenantName');
      //console.log('storedTenantName', storedTenantName);
      const storedActiveUserCount = parseInt(sessionStorage.getItem('activeUserCount') || '0', 10);
      const storedTicketsResolved = parseInt(sessionStorage.getItem('ticketsResolvedAndClosed') || '0', 10);
      const storedTicketsRaised = parseInt(sessionStorage.getItem('ticketsRaisedLast30Days') || '0', 10);

      if (storedTenantName) {
        setTenantName(storedTenantName);
      }
      if (storedActiveUserCount > 0) {
        setActiveUserCount(storedActiveUserCount);
      }
      if (storedTicketsResolved > 0) {
        setTicketsResolvedAndClosed(storedTicketsResolved);
      }
      if (storedTicketsRaised > 0) {
        setTicketsRaisedLast30Days(storedTicketsRaised);
      }
    };

    // Update immediately on mount
    updateMetricsFromStorage();

    // Also update when organizationData changes
    if (organizationData) {
      updateMetricsFromStorage();
    }
  }, [organizationData]);

  // Registration modal: keep email empty and re-apply readOnly gate when opened (reduces autofill)
  useEffect(() => {
    if (showCustomerSignupModal) {
      setSignupEmailReadOnly(true);
      setSignupFormData((prev) => ({ ...prev, email: '' }));
      setShowSignupPassword(false);
    }
  }, [showCustomerSignupModal]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Fetch organization by domain on landing page load
  useEffect(() => {
    const fetchOrganizationByDomain = async () => {
      try {
        // Extract domain from current hostname
        const hostname = window.location.hostname;
        // const hostname='platform.example.com';
        // For localhost, use a test domain or allow it to proceed
        // In production, this will use the actual domain
        let domainToUse = hostname;

        if (hostname === 'localhost' ) {
          //console.log('Skipping organization lookup for localhost');
          domainToUse='https://helpdesk-pro.dhanushinfotech.com/';
        }


        // For localhost development, you can use a test domain or skip
        // Uncomment the line below to skip localhost calls:
        // if (hostname === 'localhost' || hostname === '127.0.0.1') {
        //   //console.log('Skipping organization lookup for localhost');
        //   return;
        // }

        //console.log('🔍 Fetching organization for domain:', domainToUse);
        setOrganizationLoading(true);
        setOrganizationError(null);

        // Call the API with the domain name
        const response = await getOrganizationByDomain(domainToUse);
        
        //console.log('📥 Organization API response (full):', JSON.stringify(response, null, 2));
        
        // The API backend returns: { status: "OK", message: "...", data: { tenantId, tenantName, ... } }
        // wrapResponse wraps it: { data: { status, message, data: {...} } }
        // So the actual tenant data is at: response.data.data
        let tenantData = null;
        
        if (response && response.data) {
          // Check if response.data has a nested 'data' property (from backend ResponseBean)
          if (response.data.data && typeof response.data.data === 'object' && response.data.data.tenantId) {
            // Nested structure: { data: { status, message, data: { tenantId, ... } } }
            tenantData = response.data.data;
            //console.log('✅ Using nested data structure (response.data.data)');
          } else if (response.data.tenantId || response.data.tenantName) {
            // Direct structure: { data: { tenantId, tenantName, ... } }
            tenantData = response.data;
            //console.log('✅ Using direct data structure (response.data)');
          } else {
            console.warn('⚠️ Unexpected response structure:', response);
          }
        }
        
        if (tenantData) {
          setOrganizationData(tenantData);
          //console.log('✅ Organization data loaded:', tenantData);
          //console.log('✅ Tenant Name:', tenantData.tenantName);
          //console.log('✅ Active User Count:', tenantData.activeUserCount);
          //console.log('✅ Tickets Resolved:', tenantData.ticketsResolvedAndClosed);
          //console.log('✅ Tickets Raised:', tenantData.ticketsRaisedLast30Days);
          
          // Save complete organization data to sessionStorage
          try {
            const organizationDataJson = JSON.stringify(tenantData);
            sessionStorage.setItem('organizationData', organizationDataJson);
            //console.log('💾 Stored complete organization data in sessionStorage');
            
            // Also save individual fields for easy access and backward compatibility
            if (tenantData.tenantId) {
              sessionStorage.setItem('tenantId', tenantData.tenantId.toString());
              //console.log('💾 Stored tenantId in sessionStorage:', tenantData.tenantId);
            }
            
            if (tenantData.tenantName) {
              sessionStorage.setItem('tenantName', tenantData.tenantName);
              setTenantName(tenantData.tenantName);
              //console.log('💾 Stored tenantName in sessionStorage:', tenantData.tenantName);
            }
            
            if (tenantData.tenantCode) {
              sessionStorage.setItem('tenantCode', tenantData.tenantCode);
            }
            
            if (tenantData.domain) {
              sessionStorage.setItem('domain', tenantData.domain);
            }
            
            // Save statistics for dashboard/metrics and update state
            if (tenantData.activeUserCount !== undefined && tenantData.activeUserCount !== null) {
              sessionStorage.setItem('activeUserCount', tenantData.activeUserCount.toString());
              setActiveUserCount(tenantData.activeUserCount);
              //console.log('💾 Stored activeUserCount in sessionStorage:', tenantData.activeUserCount);
            }
            
            if (tenantData.ticketsResolvedAndClosed !== undefined && tenantData.ticketsResolvedAndClosed !== null) {
              sessionStorage.setItem('ticketsResolvedAndClosed', tenantData.ticketsResolvedAndClosed.toString());
              setTicketsResolvedAndClosed(tenantData.ticketsResolvedAndClosed);
              //console.log('💾 Stored ticketsResolvedAndClosed in sessionStorage:', tenantData.ticketsResolvedAndClosed);
            }
            
            if (tenantData.ticketsRaisedLast30Days !== undefined && tenantData.ticketsRaisedLast30Days !== null) {
              sessionStorage.setItem('ticketsRaisedLast30Days', tenantData.ticketsRaisedLast30Days.toString());
              setTicketsRaisedLast30Days(tenantData.ticketsRaisedLast30Days);
              //console.log('💾 Stored ticketsRaisedLast30Days in sessionStorage:', tenantData.ticketsRaisedLast30Days);
            }
            
            //console.log('💾 All organization data saved to sessionStorage successfully');
            
            // Verify what was saved
            const savedTenantName = sessionStorage.getItem('tenantName');
            //console.log('🔍 Verification - tenantName in sessionStorage:', savedTenantName);
          } catch (storageError) {
            console.error('❌ Error saving organization data to sessionStorage:', storageError);
          }
        } else {
          console.warn('⚠️ No tenant data found in response:', response);
        }
      } catch (error) {
        console.error('❌ Failed to fetch organization by domain:', error);
        setOrganizationError(error.message || 'Failed to load organization information');
        // Don't show error toast for 404 (domain not found) - it's expected for some domains
        if (error.message && !error.message.includes('not found')) {
          // Only log, don't show error to user on landing page
          console.warn('⚠️ Organization lookup failed (non-404 error):', error.message);
        } else {
          //console.log('ℹ️ Organization not found for domain (this is normal for some domains)');
        }
      } finally {
        setOrganizationLoading(false);
      }
    };

    fetchOrganizationByDomain();
  }, []); // Run once on mount

  useEffect(() => {
    const loadGoogleClientId = async () => {
      try {
        const tenantId = parseInt(sessionStorage.getItem('tenantId') || '1', 10);
        const response = await registrationService.getGoogleClientId(tenantId);
        const responseData = response?.data || response;
        const clientId =
          responseData?.data?.clientId ||
          responseData?.clientId ||
          responseData?.data?.data?.clientId;
        if (clientId) {
          setGoogleClientId(clientId);
        }
      } catch (err) {
        console.warn('Failed to load Google client id:', err);
      }
    };

    loadGoogleClientId();
  }, [organizationData]);

  // Reset token countdown timer
  useEffect(() => {
    if (showResetPasswordModal && resetTokenGeneratedAt) {
      // Calculate remaining time
      const calculateRemaining = () => {
        const now = new Date().getTime();
        const elapsed = Math.floor((now - resetTokenGeneratedAt) / 1000);
        const remaining = Math.max(0, 300 - elapsed); // 5 minutes = 300 seconds
        return remaining;
      };

      // Set initial countdown
      setResetTokenCountdown(calculateRemaining());

      // Update countdown every second
      const timer = setInterval(() => {
        const remaining = calculateRemaining();
        setResetTokenCountdown(remaining);
        if (remaining === 0) {
          clearInterval(timer);
        }
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [showResetPasswordModal, resetTokenGeneratedAt]);

  // Check for email query parameter on mount
  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setLoginEmail(emailParam);
      // Scroll to login section
      setTimeout(() => {
        const element = document.querySelector('#login-section');
        if (element) {
          const headerOffset = 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [searchParams]);

  // Validate email and phone
  const validateEmail = (email) => {
    email = email.trim().toLowerCase();
    if (!email || !email.trim()) {
      return 'Email is required';
    }
    if (!email.includes('@')) {
      return 'Email must contain @';
    }
    if (!email.includes('.com')) {
      return 'Email must contain .com';
    }
    return '';
  };

  const validatePhone = (phone) => {
    if (!phone || !phone.trim()) {
      return 'Mobile number is required';
    }
    // Remove any non-digit characters for validation
    const digitsOnly = phone.replace(/\D/g, '');
    if (digitsOnly.length !== 9 && digitsOnly.length !== 10) {
      return 'Mobile number must be 9 or 10 digits';
    }
    return '';
  };

  const handleGoogleOAuthSignupLogin = async () => {
    try {
      const tenantId = parseInt(sessionStorage.getItem('tenantId') || '1', 10);
      if (!googleClientId) {
        toast.error('Google login is not configured for this tenant.');
        return;
      }
      if (!window.google || !window.google.accounts || !window.google.accounts.id) {
        toast.error('Google login library is not loaded. Please refresh and try again.');
        return;
      }

      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: async (googleResponse) => {
          try {
            if (!googleResponse?.credential) {
              toast.error('Google login failed. Missing token.');
              return;
            }
            let authResponse;
            try {
              authResponse = await authService.googleOAuthLogin(
                googleResponse.credential,
                tenantId,
                googleClientId,
                beginSessionConflictAttempt()
              );
            } catch (loginErr) {
              const msg = extractErrorMessage(loginErr) || '';
              // If user not found, ask consent to create account
              if (msg.includes('USER_NOT_FOUND') || loginErr?.message?.includes('USER_NOT_FOUND')) {
                setPendingGoogleLogin({
                  credential: googleResponse.credential,
                  tenantId,
                  clientId: googleClientId
                });
                setShowGoogleConsentModal(true);
                return;
              } else {
                throw loginErr;
              }
            }

            toast.success('Google login successful');
            setShowCustomerSignupModal(false);

            if (authResponse?.userId) {
              navigateToRoleDashboard(authResponse.roles, navigate);
            } else {
              navigate('/home', { replace: true });
            }
          } catch (error) {
            if (
              error?.message === 'LOGIN_POLL_ABORTED' ||
              error?.message === 'SESSION_FLOW_TERMINATED' ||
              error?.message === 'LOGIN_REJECTED' ||
              error?.name === 'AbortError'
            ) {
              return;
            }
            const message = extractErrorMessage(error) || 'Google login failed';
            toast.error(message);
            setSignupError(message);
          }
        },
      });

      window.google.accounts.id.prompt();
    } catch (error) {
      const message = extractErrorMessage(error) || 'Google login failed';
      toast.error(message);
      setSignupError(message);
    }
  };

  const handleConfirmGoogleAccountCreation = async () => {
    try {
      if (!pendingGoogleLogin?.credential || !pendingGoogleLogin?.tenantId || !pendingGoogleLogin?.clientId) {
        setShowGoogleConsentModal(false);
        setPendingGoogleLogin(null);
        return;
      }

      await registrationService.googleOauthUpsert({
        idToken: pendingGoogleLogin.credential,
        tenantId: pendingGoogleLogin.tenantId,
        clientId: pendingGoogleLogin.clientId,
        source: 'landing_signup'
      });

      const authResponse = await authService.googleOAuthLogin(
        pendingGoogleLogin.credential,
        pendingGoogleLogin.tenantId,
        pendingGoogleLogin.clientId,
        beginSessionConflictAttempt()
      );

      toast.success('Google login successful');
      setShowGoogleConsentModal(false);
      setPendingGoogleLogin(null);

      if (authResponse?.userId) {
        navigateToRoleDashboard(authResponse.roles, navigate);
      } else {
        navigate('/home', { replace: true });
      }
    } catch (error) {
      if (
        error?.message === 'LOGIN_POLL_ABORTED' ||
        error?.message === 'SESSION_FLOW_TERMINATED' ||
        error?.message === 'LOGIN_REJECTED' ||
        error?.name === 'AbortError'
      ) {
        return;
      }
      const message = extractErrorMessage(error) || 'Account creation failed';
      toast.error(message);
      setSignupError(message);
      setShowGoogleConsentModal(false);
      setPendingGoogleLogin(null);
    }
  };

  const handleCancelGoogleAccountCreation = () => {
    setShowGoogleConsentModal(false);
    setPendingGoogleLogin(null);
    toast('Account was not created.');
  };

  // Handle customer signup form submission
  const handleCustomerSignup = async (e) => {
    e.preventDefault();
    setSignupError('');
    
    // Validate email and phone
    const emailError = validateEmail(signupFormData.email);
    const phoneError = validatePhone(signupFormData.phone);
    
    setFieldErrors({
      email: emailError,
      phone: phoneError
    });

    // If there are validation errors, don't proceed
    if (emailError || phoneError) {
      if (emailError) {
        toast.error(emailError);
      }
      if (phoneError) {
        toast.error(phoneError);
      }
      return;
    }

    if (!isDefaultPasswordValid(signupFormData.defaultPassword)) {
      return;
    }

    setSignupLoading(true);

    try {
      // Generate username from email if not provided
      let username = signupFormData.username.trim();
      if (!username) {
        username = signupFormData.email.split('@')[0].toLowerCase().replace(/[^a-z0-9]/g, '');
      }

      // Split fullName into firstName and lastName if not provided
      let firstName = signupFormData.firstName.trim();
      let lastName = signupFormData.lastName.trim();
      if (!firstName || !lastName) {
        const nameParts = signupFormData.fullName.trim().split(' ');
        if (nameParts.length >= 2) {
          firstName = firstName || nameParts[0];
          lastName = lastName || nameParts.slice(1).join(' ');
        } else {
          firstName = firstName || signupFormData.fullName.trim();
          lastName = lastName || '';
        }
      }

      // Get tenantId (default to 1 if not available)
      const tenantId = parseInt(sessionStorage.getItem('tenantId') || '1', 10);

      // Use password from form or generate a secure default password
      const defaultPassword = signupFormData.defaultPassword.trim() || `TempPass${Math.random().toString(36).slice(-8)}!`;

      // Prepare payload
      const payload = {
        email: signupFormData.email.trim(),
        username: username,
        defaultPassword: defaultPassword,
        fullName: signupFormData.fullName.trim(),
        firstName: firstName,
        lastName: lastName,
        phone: signupFormData.phone.trim(),
        // role: 'CUSTOMER',
        roleId: 5,
        tenantId: tenantId
      };

      // Call the API
      const response = await registrationService.registerCustomerNoAuth(payload);
      
      // Check for success - backend returns status 201 (CREATED) on success
      // Response structure from registrationService: { data: { status: 201, message: "...", data: {...} } }
      // Or direct: { status: 201, message: "...", data: {...} }
      const responseData = response?.data || response;
      const statusCode = responseData?.status;
      const statusValue = typeof statusCode === 'number' ? statusCode : (statusCode?.value || (statusCode === 'CREATED' ? 201 : null));
      const message = responseData?.message || '';
      
      // Check if it's a success (201 CREATED or message contains "successfully")
      const isSuccess = statusValue === 201 || statusCode === 'CREATED' || 
                       (message && message.toLowerCase().includes('successfully')) ||
                       (responseData?.data && statusValue !== 400 && statusValue !== 409 && statusValue !== 500);
      
      if (isSuccess) {
        toast.success('Account created successfully! You can now login with your credentials.');
        setShowCustomerSignupModal(false);
        setSignupError('');
        setFieldErrors({ email: '', phone: '' });
        setSignupEmailReadOnly(true);
        // Reset form
        setSignupFormData({
          fullName: '',
          firstName: '',
          lastName: '',
          email: '',
          username: '',
          phone: '',
          defaultPassword: '',
          role: 'CUSTOMER'
        });
      } else {
        const errorMessage = message || responseData?.message || 'Failed to create account. Please try again.';
        setSignupError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Signup error:', error);
      // Check if it's actually an error or a successful response that was caught
      const responseData = error?.response?.data || error?.data;
      const statusCode = responseData?.status?.value || responseData?.status;
      
      if (statusCode === 201 || statusCode === 'CREATED' || responseData?.message?.toLowerCase().includes('successfully')) {
        // This is actually a success, not an error
        toast.success('Account created successfully! You can now login with your credentials.');
        setShowCustomerSignupModal(false);
        setSignupError('');
        setFieldErrors({ email: '', phone: '' });
        setSignupEmailReadOnly(true);
        // Reset form
        setSignupFormData({
          fullName: '',
          firstName: '',
          lastName: '',
          email: '',
          username: '',
          phone: '',
          defaultPassword: '',
          role: 'CUSTOMER'
        });
      } else {
        // This is a real error
        const errorMessage = responseData?.message || error?.message || 'Failed to create account. Please try again.';
        setSignupError(errorMessage);
        toast.error(errorMessage);
      }
    } finally {
      setSignupLoading(false);
    }
  };

  const customerSignupPhoneInvalid = validatePhone(signupFormData.phone) !== '';
  const customerSignupPasswordInvalid =
    !signupFormData.defaultPassword || !isDefaultPasswordValid(signupFormData.defaultPassword);
  const customerSignupSubmitDisabled =
    signupLoading ||
    !signupFormData.fullName ||
    !signupFormData.email ||
    customerSignupPasswordInvalid ||
    customerSignupPhoneInvalid;

  // Smooth scroll handler for navigation links
  const handleNavClick = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      const headerOffset = 80; // Account for fixed header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      // Close mobile menu if open
      setMobileOpen(false);
    }
  };

  // Step 1: Check if user exists and determine flow
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // FIRST: Check user status (don't request OTP yet)
      // This prevents unnecessary OTP generation for users who can login with password
      const userStatus = await authService.checkUserStatus(loginEmail);

      if (!userStatus.exists) {
        setError('User not found. Please check your email address.');
        toast.error('User not found');
        return;
      }

      if (!userStatus.isActive) {
        setError('Account is not active. Please contact administrator.');
        toast.error('Account is not active');
        return;
      }

      // Determine flow based on password status
      if (!userStatus.isPasswordChanged) {
        // First-time user: Request OTP for first-time login flow
        setIsFirstTimeUser(true);
        const otpResponse = await authService.requestOtp(loginEmail);
        // Store expiration time if provided
        if (otpResponse?.expiresAt) {
          setOtpExpiresAt(otpResponse.expiresAt);
        }
        setStep('otp');
        toast.success('OTP sent to your email!');
      } else {
        // Subsequent user: Show login choice (password or OTP)
        // Don't request OTP yet - let user choose their preferred method
        setIsFirstTimeUser(false);
        setStep('login-choice');
      }

    } catch (error) {
      console.error('Error checking user status:', error);
      const errorMessage = extractErrorMessage(error) || 'Failed to check user status';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Request OTP for subsequent login
  const handleRequestOtp = async () => {
    setError('');
    setLoading(true);

    try {
      const otpResponse = await authService.requestOtp(loginEmail);
      // Store expiration time if provided
      if (otpResponse?.expiresAt) {
        setOtpExpiresAt(otpResponse.expiresAt);
      }
      toast.success('OTP sent to your email!');
      setStep('otp');
    } catch (error) {
      console.error('Error requesting OTP:', error);
      const errorMessage = extractErrorMessage(error) || 'Failed to send OTP';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: OTP Verified
  const handleOtpVerified = async (requiresPasswordChange, otpCode, verificationResponse = null) => {
    setRequiresPasswordChange(requiresPasswordChange);
    if (otpCode) {
      setVerifiedOtp(otpCode); // Store verified OTP for password change
    }

    if (requiresPasswordChange) {
      // First-time user: Go to password change
      setStep('change-password');
    } else {
      // Subsequent user: OTP already verified in OtpVerification component
      // Use the response from the first verification instead of calling verifyOtp again
      try {
        // If verificationResponse is provided, use it (token already obtained)
        // Otherwise, fallback to calling verifyOtp (shouldn't happen in normal flow)
        const response =
          verificationResponse ||
          (await authService.verifyOtp(loginEmail, otpCode, beginSessionConflictAttempt()));

        if (response.token) {
          const user = authService.getCurrentUser();
          if (user) {
            const userJson = JSON.stringify(user);
            // Store in both localStorage and sessionStorage
            // localStorage.setItem('authUser', userJson);
            sessionStorage.setItem('authUser', userJson);

            // Register FCM token after successful login (for all roles)
            MessagingService.registerTokenAfterLogin().catch(err => {
              console.warn('FCM token registration failed:', err);
              // Don't block login if token registration fails
            });

            // CRITICAL: Dispatch login event to trigger notification fetch
            window.dispatchEvent(new CustomEvent('user-logged-in'));
            sessionStorage.setItem("wasAuthenticated", "true");
            //console.log("🔔 Login event dispatched - NotificationContext will fetch notifications");

            navigateToRoleDashboard(user.role, navigate);
            toast.success('Login successful!');
          } else {
            throw new Error('Failed to decode user information');
          }
        }
      } catch (error) {
        if (
          error?.message === 'LOGIN_POLL_ABORTED' ||
          error?.message === 'SESSION_FLOW_TERMINATED' ||
          error?.message === 'LOGIN_REJECTED' ||
          error?.name === 'AbortError'
        ) {
          return;
        }
        console.error('OTP verification error:', error);
        const errorMessage = extractErrorMessage(error) || 'OTP verification failed';
        toast.error(errorMessage);
        setError(errorMessage);
      }
    }
  };

  // Step 3a: Resend OTP
  const handleResendOtp = async () => {
    try {
      const otpResponse = await authService.requestOtp(loginEmail);
      // Store expiration time if provided
      if (otpResponse?.expiresAt) {
        setOtpExpiresAt(otpResponse.expiresAt);
      }
    } catch (error) {
      console.error('Error resending OTP:', error);
      const errorMessage = extractErrorMessage(error) || 'Failed to resend OTP';
      toast.error(errorMessage);
    }
  };

  // Step 3b: Password Changed (first-time)
  const handlePasswordChanged = () => {
    // Token already stored in ChangePassword component via authService
    // Navigate to appropriate dashboard based on role
    const user = authService.getCurrentUser();
    if (user && user.role) {
      // Register FCM token after successful password change and login (for all roles)
      MessagingService.registerTokenAfterLogin().catch(err => {
        console.warn('FCM token registration failed:', err);
        // Don't block navigation if token registration fails
      });

      // CRITICAL: Dispatch login event to trigger notification fetch
      window.dispatchEvent(new CustomEvent('user-logged-in'));
      sessionStorage.setItem("wasAuthenticated", "true");
      //console.log("🔔 Login event dispatched - NotificationContext will fetch notifications");

      navigateToRoleDashboard(user.role, navigate);
      toast.success('Password changed successfully!');
    } else {
      // Fallback navigation
      navigate('/portal');
      toast.error('Failed to get user information');
    }
  };

  // Step 3c: Login with Password (subsequent login - direct)
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authService.login(
        loginEmail,
        password,
        null,
        null,
        beginSessionConflictAttempt()
      );

      if (response.token) {
        const user = authService.getCurrentUser();
        if (user) {
          const userJson = JSON.stringify(user);
          // Store in both localStorage and sessionStorage
          // localStorage.setItem('authUser', userJson);
          sessionStorage.setItem('authUser', userJson);
          // localStorage.setItem('username', user.username);
          sessionStorage.setItem('username', user.username);

          // Register FCM token after successful login (for all roles)
          MessagingService.registerTokenAfterLogin().catch(err => {
            console.warn('FCM token registration failed:', err);
            // Don't block login if token registration fails
          });

          // CRITICAL: Dispatch login event to trigger notification fetch
          window.dispatchEvent(new CustomEvent('user-logged-in'));
          sessionStorage.setItem("wasAuthenticated", "true");
          //console.log("🔔 Login event dispatched - NotificationContext will fetch notifications");

          navigateToRoleDashboard(user.role, navigate);
          toast.success('Login successful!');
        } else {
          throw new Error('Failed to decode user information');
        }
      } else {
        throw new Error('No token received from server');
      }
    } catch (error) {
      if (
        error?.message === 'LOGIN_POLL_ABORTED' ||
        error?.message === 'SESSION_FLOW_TERMINATED' ||
        error?.message === 'LOGIN_REJECTED' ||
        error?.name === 'AbortError'
      ) {
        return;
      }
      console.error('Login error:', error);
      let errorMessage = extractErrorMessage(error) || 'Login failed';
      
      // Show user-friendly message for wrong password/credentials
      if (errorMessage.toLowerCase().includes('unauthorized') || 
          errorMessage.toLowerCase().includes('invalid') ||
          errorMessage.toLowerCase().includes('credentials') ||
          errorMessage.toLowerCase().includes('password') ||
          errorMessage.toLowerCase().includes('authentication failed')) {
        errorMessage = 'Wrong password. Please check your credentials and try again.';
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
      // Don't navigate away - stay on the same page to show the error
    } finally {
      setLoading(false);
    }
  };

  // Back navigation
  const handleBack = () => {
    if (step === 'otp') {
      if (isFirstTimeUser) {
        setStep('email');
      } else {
        setStep('login-choice');
      }
    } else if (step === 'change-password') {
      setStep('otp');
    } else if (step === 'password') {
      setStep('login-choice');
    } else if (step === 'login-choice') {
      setStep('email');
    }
  };

  // Render based on step
  const renderLoginStep = () => {
    switch (step) {
      case 'email':
        return renderEmailStep();
      case 'otp':
        return (
          <OtpVerification
            email={loginEmail}
            onVerified={handleOtpVerified}
            onResend={handleResendOtp}
            onBack={handleBack}
            expiresAt={otpExpiresAt}
            getSessionConflictOptions={beginSessionConflictAttempt}
          />
        );
      case 'change-password':
        return (
          <ChangePassword
            email={loginEmail}
            verifiedOtp={verifiedOtp} // OTP for identity verification (not used as current password)
            onPasswordChanged={handlePasswordChanged}
            onBack={handleBack}
            getSessionConflictOptions={beginSessionConflictAttempt}
          />
        );
      case 'password':
        return renderPasswordStep();
      case 'login-choice':
        return renderLoginChoiceStep();
      default:
        return renderEmailStep();
    }
  };

  // Email step render
  const renderEmailStep = () => {
    return (
      <>
        {/* Header with Logo */}
        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{
              boxShadow: '0 4px 12px rgba(7, 17, 54, 0.3)'
            }}
          >
               <img
                src={logo}
                alt="Shield"
                className="h-10 w-10"
              />
            {/* <Shield className="w-7 h-7 text-white" fill="white" /> */}
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#1a365d' }}>{tenantName.toUpperCase()}</h1>
            <p className="text-sm font-semibold hidden" style={{ color: '#006600' }}>SIGN IN</p>
          </div>
        </div>

        <form onSubmit={handleEmailSubmit} className="mt-8 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: '#4a5568' }}>
              EMAIL OR USERNAME
            </label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: '#718096' }} />
              <input
                type="email"
                required
                className="w-full pl-12 pr-4 py-3.5 rounded-xl border-2 transition-all focus:outline-none"
                style={{
                  borderColor: error ? '#ef4444' : '#e2e8f0',
                  background: 'rgba(255, 255, 255, 0.9)',
                  color: '#1a202c'
                }}
                placeholder="Enter your email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value.toLowerCase())}
                onFocus={(e) => {
                  e.target.style.borderColor = '#006600';
                  e.target.style.background = '#ffffff';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = error ? '#ef4444' : '#e2e8f0';
                  e.target.style.background = 'rgba(255, 255, 255, 0.9)';
                }}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || !loginEmail}
            className="w-full py-4 rounded-xl font-bold text-white text-base transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            style={{
              background: loading || !loginEmail
                ? 'linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)'
                : 'linear-gradient(135deg,#006600 0%,#BB0000 100%)',
              boxShadow: loading || !loginEmail
                ? '0 4px 12px rgba(156, 163, 175, 0.3)'
                : '0 4px 20px rgba(7, 17, 54, 0.4)'
            }}
          >
            {/* {loading ? 'SENDING OTP...' : 'CONTINUE'} */}
            {loading ? 'CHECKING USER STATUS...' : 'CONTINUE'}
          </button>

          {/* Google Sign-In Button */}
          <div className="mt-3">
            <button
              type="button"
              onClick={handleGoogleOAuthSignupLogin}
              className="w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-3 transition-all border"
              style={{
                background: '#ffffff',
                borderColor: '#e2e8f0',
                color: '#1f2937',
              }}
            >
              <FcGoogle size={22} />
              <span>Sign in with Google</span>
            </button>
          </div>

          <div className="text-center pt-2">
            <p className="text-sm" style={{ color: '#718096' }}>
              Don't have an account?{' '}
              <button
                type="button"
                onClick={() => {
                  setSignupFormData({
                    fullName: '',
                    firstName: '',
                    lastName: '',
                    email: '',
                    username: '',
                    phone: '',
                    defaultPassword: '',
                    role: 'CUSTOMER'
                  });
                  setFieldErrors({ email: '', phone: '' });
                  setSignupError('');
                  setSignupEmailReadOnly(true);
                  setShowCustomerSignupModal(true);
                }}
                className="font-semibold transition-colors bg-transparent border-none p-0 cursor-pointer"
                style={{ color: '#006600' }}
                onMouseEnter={(e) => e.target.style.color = '#071136'}
                onMouseLeave={(e) => e.target.style.color = '#006600'}
              >
                SIGN UP HERE
              </button>
            </p>
          </div>
        </form>
      </>
    );
  };

  // Login choice step render (for subsequent logins)
  const renderLoginChoiceStep = () => {
    return (
      <>
        {/* Header with Logo */}
        <div className="flex items-center gap-3 mb-2">
          {/* <div
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg,#BB0000 0%,#BB0000 100%)',
              boxShadow: '0 4px 12px rgba(48, 122, 145, 0.3)'
            }}
          >
            <Shield className="w-7 h-7 text-white" fill="white" />
          </div> */}
           <div className="flex h-10 w-10 items-center justify-center rounded-2xl">
              <img
                src={logo}
                alt="Shield"
                className="h-10 w-10 object-contain"
              />
            </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#071136' }}>{tenantName.toUpperCase()}</h1>
            <p className="text-sm font-semibold hidden" style={{ color: '#071136' }}>SIGN IN</p>
          </div>
        </div>

        <div className="mt-8 space-y-6">
          <div>
            <h2 className="text-xl font-bold mb-2" style={{ color: '#1a202c' }}>
              Choose Login Method
            </h2>
            <p className="text-sm" style={{ color: '#718096' }}>
              How would you like to sign in?
            </p>
          </div>

          <div className="space-y-4">
            {/* Password Login Option */}
            <button
              onClick={() => setStep('password')}
              className="w-full py-4 px-6 rounded-xl border-2 transition-all text-left transform hover:scale-[1.02]"
              style={{
                borderColor: '#071136',
                background: 'rgba(255, 255, 255, 0.9)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(7, 17, 54, 0.05)';
                e.currentTarget.style.borderColor = '#006600';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                e.currentTarget.style.borderColor = '#071136';
              }}
            >
              <div className="font-semibold mb-1" style={{ color: '#1a202c' }}>
                Login with Password
              </div>
              <div className="text-sm" style={{ color: '#718096' }}>
                Enter your password to sign in
              </div>
            </button>

            {/* OTP Login Option */}
            <button
              onClick={handleRequestOtp}
              disabled={loading}
              className="w-full py-4 px-6 rounded-xl border-2 transition-all text-left transform hover:scale-[1.02]"
              style={{
                borderColor: '#071136',
                background: 'rgba(255, 255, 255, 0.9)',
                opacity: loading ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.background = 'rgba(7, 17, 54, 0.05)';
                  e.currentTarget.style.borderColor = '#006600';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.borderColor = '#071136';
                }
              }}
            >
              <div className="font-semibold mb-1" style={{ color: '#1a202c' }}>
                Login with OTP
              </div>
              <div className="text-sm" style={{ color: '#718096' }}>
                Receive a verification code via email
              </div>
            </button>
          </div>

          {/* Back Button */}
          <button
            type="button"
            onClick={handleBack}
            className="w-full mt-6 text-sm text-center transition-colors"
            style={{ color: '#718096' }}
            onMouseEnter={(e) => e.target.style.color = '#071136'}
            onMouseLeave={(e) => e.target.style.color = '#718096'}
          >
            ← Back to email entry
          </button>
        </div>
      </>
    );
  };

  // Password step render (for subsequent logins)
  const renderPasswordStep = () => {
    return (
      <>
        {/* Header with Logo */}
        <div className="flex items-center gap-3 mb-2">
          {/* <div
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg,#BB0000 0%,#BB0000 100%)',
              boxShadow: '0 4px 12px rgba(48, 122, 145, 0.3)'
            }}
          >
            <Shield className="w-7 h-7 text-white" fill="white" />
          </div> */}
           <div className="flex h-10 w-10 items-center justify-center rounded-2xl">
              <img
                src={logo}
                alt="Shield"
                className="h-10 w-10 object-contain"
              />
            </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#071136' }}>{tenantName.toUpperCase()}</h1>
            <p className="text-sm font-semibold" style={{ color: '#071136' }}>SIGN IN</p>
          </div>
        </div>

        <form onSubmit={handlePasswordSubmit} className="mt-8 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Email/Username Field */}
          <div>
            <label className="block text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: '#4a5568' }}>
              EMAIL OR USERNAME
            </label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: '#718096' }} />
              <input
                type="text"
                required
                className="w-full pl-12 pr-4 py-3.5 rounded-xl border-2 transition-all focus:outline-none"
                style={{
                  borderColor: error ? '#ef4444' : '#e2e8f0',
                  background: 'rgba(255, 255, 255, 0.9)',
                  color: '#1a202c'
                }}
                placeholder="Enter your email or username"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                onFocus={(e) => {
                  e.target.style.borderColor = '#006600';
                  e.target.style.background = '#ffffff';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = error ? '#ef4444' : '#e2e8f0';
                  e.target.style.background = 'rgba(255, 255, 255, 0.9)';
                }}
              />
            </div>
          </div>

          {/* Password Field */}
          <div>
            <label className="block text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: '#4a5568' }}>
              PASSWORD
            </label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: '#718096' }} />
              <input
                type={showPassword ? 'text' : 'password'}
                required
                className="w-full pl-12 pr-12 py-3.5 rounded-xl border-2 transition-all focus:outline-none"
                style={{
                  borderColor: error ? '#ef4444' : '#e2e8f0',
                  background: 'rgba(255, 255, 255, 0.9)',
                  color: '#1a202c'
                }}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={(e) => {
                  e.target.style.borderColor = '#006600';
                  e.target.style.background = '#ffffff';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = error ? '#ef4444' : '#e2e8f0';
                  e.target.style.background = 'rgba(255, 255, 255, 0.9)';
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 transition-colors"
                style={{ color: '#718096' }}
                onMouseEnter={(e) => e.target.style.color = '#071136'}
                onMouseLeave={(e) => e.target.style.color = '#718096'}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Remember Me & Forgot Password */}
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border-2"
                style={{
                  borderColor: rememberMe ? '#071136' : '#cbd5e0',
                  accentColor: '#071136'
                }}
              />
              <span className="text-sm font-semibold" style={{ color: '#4a5568' }}>REMEMBER ME</span>
            </label>
            <button
              type="button"
              onClick={() => {
                setShowForgotPasswordModal(true);
                setForgotPasswordEmail(loginEmail || '');
                setForgotPasswordError('');
                setForgotPasswordSuccess(false);
              }}
              className="text-sm font-semibold transition-colors"
              style={{ color: '#006600' }}
              onMouseEnter={(e) => e.target.style.color = '#071136'}
              onMouseLeave={(e) => e.target.style.color = '#006600'}
            >
              FORGOT PASSWORD?
            </button>
          </div>

          {/* Sign In Button */}
          <button
            type="submit"
            disabled={loading || !loginEmail || !password}
            className="w-full py-4 rounded-xl font-bold text-white text-base transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            style={{
              background: loading || !loginEmail || !password
                ? 'linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)'
                : 'linear-gradient(135deg,#006600 0%,#BB0000 100%)',
              boxShadow: loading || !loginEmail || !password
                ? '0 4px 12px rgba(156, 163, 175, 0.3)'
                : '0 4px 20px rgba(7, 17, 54, 0.4)'
            }}
          >
            {loading ? 'SIGNING IN...' : 'SIGN IN'}
          </button>

          {/* Sign Up Link */}
          <div className="text-center pt-2">
            <p className="text-sm" style={{ color: '#718096' }}>
              Don't have an account?{' '}
              <Link
                to="/signup"
                className="font-semibold transition-colors"
                style={{ color: '#071136' }}
                onMouseEnter={(e) => e.target.style.color = '#071136'}
                onMouseLeave={(e) => e.target.style.color = '#071136'}
              >
                SIGN UP HERE
              </Link>
            </p>
          </div>

          {/* Back Button */}
          <button
            type="button"
            onClick={handleBack}
            className="w-full text-sm text-center transition-colors"
            style={{ color: '#718096' }}
            onMouseEnter={(e) => e.target.style.color = '#071136'}
            onMouseLeave={(e) => e.target.style.color = '#718096'}
          >
            ← Back to login options
          </button>
        </form>
      </>
    );
  };

  return (
    <div className="relative min-h-screen bg-[#F8FCFB] text-[#0B2530]" style={{ scrollBehavior: 'smooth' }}>
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[460px] bg-gradient-to-b from-[#E0F8F3] via-white/60 to-transparent" />

      {/* Primary Navigation */}
      <header
        className={`fixed inset-x-0 top-0 z-40 transition-all duration-300 ${scrolled ? 'bg-white/95 shadow-sm backdrop-blur' : 'bg-transparent'
          }`}
      >
        <div className="relative mx-auto flex max-w-6xl items-center px-6 py-4">
          <Link to="/" className="flex items-center gap-2 text-lg font-semibold">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl">
              <img
                src={logo}
                alt="Shield"
                className="h-10 w-10 object-contain"
              />
            </div>
            {tenantName}
          </Link>

          <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-8 text-sm font-medium text-[#071136] lg:flex">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="hover:text-[#006600] transition-colors cursor-pointer"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <button
            className="rounded-lg border border-[#E2EBEF] p-2 text-[#0B2530] lg:hidden"
            onClick={() => setMobileOpen((prev) => !prev)}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {mobileOpen && (
          <div className="border-t border-[#E5EFF3] bg-white px-6 py-4 lg:hidden">
            <nav className="flex flex-col gap-4 text-sm font-medium text-[#4A5B63]">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => {
                    handleNavClick(e, link.href);
                    setMobileOpen(false);
                  }}
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </div>
        )}
      </header>

      <main className="relative z-10 pt-28 lg:pt-32">
        {/* Login Section with Marketing Text */}
        <section id="login-section" className="mx-auto max-w-7xl px-6 py-8 lg:py-10 scroll-mt-20">
          <div className="grid gap-8 lg:grid-cols-2 lg:items-center lg:gap-12">
            {/* Left Side - Marketing Text */}
            <div className="space-y-6">
              <h1 className="text-5xl lg:text-6xl xl:text-7xl font-black leading-tight text-[#0B2530]">
                A One-Stop Tool for HelpDesk Management
              </h1>
              <p className="text-base lg:text-lg xl:text-xl leading-relaxed text-[#4A5B63]">
                HelpDesk integrates ticket management, knowledge base, automation, reporting, and many other features in one platform. Everyone can work smoothly and efficiently on HelpDesk.
              </p>
            </div>

            {/* Right Side - Login Card */}
            <div className="flex justify-center lg:justify-end">
              <div className="w-full max-w-md">
                {/* Render Current Step */}
                {step === 'password' ? (
                  <div
                    className="rounded-2xl p-8 sm:p-10 backdrop-blur-xl"
                    style={{
                      background: 'rgba(255, 255, 255, 0.95)',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.5)',
                      border: '1px solid rgba(255, 255, 255, 0.3)'
                    }}
                  >
                    {renderPasswordStep()}
                  </div>
                ) : (
                  <div
                    className="rounded-2xl p-8 sm:p-10 backdrop-blur-xl"
                    style={{
                      background: 'rgba(255, 255, 255, 0.95)',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.5)',
                      border: '1px solid rgba(255, 255, 255, 0.3)'
                    }}
                  >
                    {renderLoginStep()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Hero */}
        <section id="home" className="mx-auto grid max-w-6xl gap-10 px-6 py-8 lg:py-12 scroll-mt-20 lg:grid-cols-[minmax(0,1fr)_420px] lg:items-center">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-[#006600]">Customer Experience</p>
            <h1 className="mt-6 text-4xl lg:text-5xl font-bold leading-tight text-[#0B2530]">
              Quick, automated solutions for <span className="text-[#006600]">smoother support.</span>
            </h1>
            <p className="mt-8 text-xl lg:text-2xl text-[#4A5B63] leading-relaxed">
              A unified platform that transforms support operations into productivity centers, featuring powerful
              knowledge tools and seamless ticket workflows.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <a
                className="text-base lg:text-lg font-semibold text-[#0B2530] hover:text-[#006600] transition-colors"
                href="#contact"
                onClick={(e) => handleNavClick(e, '#contact')}
              >
                Contact Support →
              </a>
            </div>

            <div className="mt-6 flex flex-wrap gap-4 text-sm text-[#94A3AB]">
              {integrations.map((tool) => (
                <span key={tool} className="font-semibold">
                  {tool}
                </span>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute -right-8 top-0 hidden h-20 w-20 rounded-full bg-[#E8FCF7] blur-3xl lg:block" />
            <div className="rounded-[32px] bg-white p-6 shadow-2xl shadow-[#12302F]/10">
              <img
                src="/landing_page_image.avif"
                alt="Support collaboration"
                className="h-48 w-full rounded-2xl object-cover"
              />

              <div className="mt-6 rounded-2xl border border-[#E6F0F2] p-4">
                <div className="flex items-center justify-between text-sm font-semibold">
                  <span>My Requests</span>
                  <span className="rounded-full bg-[#FFE7E0] px-3 py-1 text-xs text-[#C94A2E]">4 Urgent</span>
                </div>
                <div className="mt-4 space-y-4 text-sm">
                  {heroRequests.map((req) => (
                    <div key={req.id} className="flex items-center justify-between gap-3 rounded-xl bg-[#F8FCFB] px-3 py-3">
                      <div>
                        <p className="font-semibold text-[#0B2530]">{req.id}: {req.title}</p>
                      </div>
                      <span className={`text-xs font-semibold ${req.accent}`}>{req.status}</span>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-[#006600] py-3 text-sm font-semibold text-white hover:bg-[#005200] transition-colors"
                  onClick={() => openKbSearchModalWithQuery(landingKbQuery || '')}
                >
                  Find Solutions Now
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-4 rounded-2xl border border-[#E6F0F2] p-4">
                <p className="text-sm font-semibold text-[#5E6C74]">Knowledge Base Search</p>
                <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-[#E5EFF3] bg-white px-3 py-2 text-sm">
                  <Search className="h-4 w-4 shrink-0 text-[#7B8D96]" />
                  <input
                    type="search"
                    value={landingKbQuery}
                    onChange={(e) => setLandingKbQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        openHeroKnowledgeSearchModal();
                      }
                    }}
                    placeholder='Try "support ticket" or "password"'
                    className="min-w-0 flex-1 bg-transparent text-[#0B2530] placeholder:text-[#7B8D96] focus:outline-none"
                    aria-label="Search knowledge base"
                  />
                  {landingKbLoading && (
                    <span className="text-xs text-[#7B8D96]">Searching…</span>
                  )}
                  <button
                    type="button"
                    onClick={openHeroKnowledgeSearchModal}
                    className="shrink-0 rounded-lg bg-[#5c2d2d] px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-[#4a2424] transition-colors sm:text-sm"
                  >
                    Search
                  </button>
                </div>
                {landingKbError && (
                  <p className="mt-2 text-xs text-red-600">{landingKbError}</p>
                )}
                <ul className="mt-3 space-y-2 text-sm text-[#4A5B63]">
                  {!(landingKbQuery || '').trim()
                    ? (featuredKbArticles.length > 0
                        ? featuredKbArticles
                        : knowledgeResults.map((title, i) => ({
                            id: `hero-kb-fallback-${i}`,
                            title
                          }))
                      ).map((row) => (
                        <li key={row.id} className="flex items-center gap-2">
                          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#006600]" />
                          <span className="line-clamp-2">{row.title}</span>
                        </li>
                      ))
                    : landingKbHits.map((row) => (
                        <li key={row.id} className="flex items-center gap-2">
                          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#006600]" />
                          <span className="line-clamp-2">{row.title}</span>
                        </li>
                      ))}
                </ul>
                {featuredKbLoading && !(landingKbQuery || '').trim() && (
                  <p className="mt-2 text-xs text-[#7B8D96]">Loading featured articles…</p>
                )}
                {!landingKbLoading &&
                  (landingKbQuery || '').trim() &&
                  landingKbHits.length === 0 &&
                  !landingKbError && (
                    <p className="mt-2 text-xs text-[#7B8D96]">No articles matched. Try different keywords.</p>
                  )}
              </div>
            </div>
          </div>
        </section>

        {/* Quick Stats Section */}
        <section id="quick-stats" className="mx-auto max-w-7xl px-6 py-10 lg:py-16 scroll-mt-20 bg-gradient-to-b from-[#F8FCFB] to-white">
          <div className="grid gap-8 lg:gap-10 md:grid-cols-3">
            {/* Active Agents Card */}
            <div className="rounded-3xl bg-white p-10 lg:p-12 shadow-xl hover:shadow-2xl transition-shadow">
              <h3 className="text-base font-bold uppercase tracking-wider text-[#4A5568] mb-6">ACTIVE AGENTS</h3>
              <div className="mb-8">
                <UserCheck className="h-12 w-12 lg:h-14 lg:w-14 text-[#006600]" />
              </div>
              <div className="flex items-baseline gap-4 mb-6">
                <span className="text-5xl lg:text-6xl font-bold text-[#1A202C]">
                  {activeUserCount > 0 ? activeUserCount.toLocaleString() : '28'}
                </span>
                <span className="text-xl lg:text-2xl font-semibold text-[#718096]">+3</span>
              </div>
              <div className="mb-4">
                <div className="h-3 bg-[#E2E8F0] rounded-full overflow-hidden">
                  <div className="h-full bg-[#006600] rounded-full" style={{ width: '75%' }}></div>
                </div>
              </div>
              <p className="text-base lg:text-lg text-[#4A5568]">Currently Online</p>
            </div>

            {/* Tickets Resolved Card */}
            <div className="rounded-3xl bg-white p-10 lg:p-12 shadow-xl hover:shadow-2xl transition-shadow">
              <h3 className="text-base font-bold uppercase tracking-wider text-[#4A5568] mb-6">TICKETS RESOLVED</h3>
              <div className="mb-8">
                <div className="flex h-14 w-14 lg:h-16 lg:w-16 items-center justify-center rounded-full bg-[#006600]">
                  <CheckCircle2 className="h-8 w-8 lg:h-9 lg:w-9 text-white" />
                </div>
              </div>
              <div className="flex items-baseline gap-4 mb-6">
                <span className="text-5xl lg:text-6xl font-bold text-[#1A202C]">
                  {ticketsResolvedAndClosed > 0 ? ticketsResolvedAndClosed.toLocaleString() : '435'}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-xl lg:text-2xl font-semibold text-[#006600]">+12%</span>
                  <TrendingUp className="h-5 w-5 lg:h-6 lg:w-6 text-[#006600]" />
                </div>
              </div>
              <p className="text-base lg:text-lg text-[#4A5568]">Successfully Closed Today</p>
            </div>

            {/* Unique Users Card */}
            <div className="rounded-3xl bg-white p-10 lg:p-12 shadow-xl hover:shadow-2xl transition-shadow">
              <h3 className="text-base font-bold uppercase tracking-wider text-[#4A5568] mb-6">UNIQUE USERS</h3>
              <div className="mb-8">
                <Users className="h-12 w-12 lg:h-14 lg:w-14 text-[#006600]" />
              </div>
              <div className="flex items-baseline gap-4 mb-6">
                <span className="text-5xl lg:text-6xl font-bold text-[#1A202C]">
                  {ticketsRaisedLast30Days > 0 ? ticketsRaisedLast30Days.toLocaleString() : '1,452'}
                </span>
                <ArrowUpRight className="h-6 w-6 lg:h-7 lg:w-7 text-[#006600]" />
              </div>
              <p className="text-base lg:text-lg text-[#4A5568]">Raised Tickets Last 30 Days</p>
            </div>
          </div>
        </section>

        {/* Feature Highlights */}
        <section id="features" className="mx-auto mt-6 max-w-6xl px-6 py-10 lg:py-16 scroll-mt-20">
          <div className="rounded-[32px] bg-white/80 p-10 lg:p-16 shadow-lg shadow-[#071136]/10">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.3em] text-[#006600]">Unified Console</p>
                <h2 className="mt-3 text-4xl lg:text-5xl font-bold text-[#0B2530]">Everything support needs, in one panel.</h2>
              </div>
              <a
                href="#knowledge"
                onClick={(e) => handleNavClick(e, '#knowledge')}
                className="text-sm font-semibold text-[#006600] hover:underline"
              >
                See knowledge experience →
              </a>
            </div>

            <div className="mt-12 lg:mt-16 grid gap-8 md:grid-cols-3">
              {featureHighlights.map((feature) => (
                <div key={feature.title} className="rounded-2xl border border-[#E6F0F2] p-8 hover:shadow-lg transition-shadow">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#E8FCF7]">
                    {feature.icon}
                  </div>
                  <h3 className="mt-6 text-xl lg:text-2xl font-semibold text-[#0B2530]">{feature.title}</h3>
                  <p className="mt-3 text-base text-[#617079]">{feature.copy}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Knowledge & Support Pillars */}
        <section id="knowledge" className="mx-auto max-w-6xl px-6 py-12 lg:py-18 scroll-mt-20">
          <div className="grid gap-12 lg:grid-cols-2">
            <div className="rounded-[32px] bg-white p-10 lg:p-12 shadow-lg shadow-[#0F2A29]/10">
              <p className="text-sm font-semibold uppercase tracking-[0.3em] text-[#006600]">Knowledge Base</p>
              <h3 className="mt-4 text-3xl lg:text-4xl font-bold text-[#0B2530]">Give every employee instant answers.</h3>
              <p className="mt-6 text-base lg:text-lg text-[#617079]">
                Organize verified content, recommended fixes, and contextual videos so people resolve their own issues faster.
              </p>

              <div className="mt-10 space-y-5">
                {(featuredKbArticles.length > 0
                  ? featuredKbArticles
                  : knowledgeResults.map((title, i) => ({
                      id: `kb-pillar-fallback-${i}`,
                      title,
                      updatedAt: null
                    }))
                ).map((row) => {
                  const dateLine =
                    row.updatedAt && formatKbArticleDate(row.updatedAt)
                      ? `Updated ${formatKbArticleDate(row.updatedAt)}`
                      : featuredKbLoading
                        ? 'Loading…'
                        : 'Knowledge base';
                  return (
                  <div
                    key={row.id}
                    role="button"
                    tabIndex={0}
                    className="flex items-center justify-between rounded-2xl border border-[#E6F0F2] px-6 py-4 hover:border-[#006600] hover:shadow-md transition-all cursor-pointer"
                    onClick={() => openKbSearchModalWithQuery(row.title || '', { syncLandingHero: false })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openKbSearchModalWithQuery(row.title || '', { syncLandingHero: false });
                      }
                    }}
                  >
                    <div>
                      <p className="text-base font-semibold text-[#0B2530]">{row.title}</p>
                      <p className="text-sm text-[#8A9AA2] mt-1">{dateLine}</p>
                    </div>
                    <ChevronRight className="h-6 w-6 text-[#8A9AA2]" aria-hidden />
                  </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-8">
              {supportPillars.map((pillar) => (
                <div key={pillar.title} className="rounded-2xl border border-[#DCE6EA] bg-white/80 p-8 hover:shadow-lg transition-shadow">
                  <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-[#E8FCF7]">
                    {pillar.icon}
                  </div>
                  <h4 className="text-xl lg:text-2xl font-semibold text-[#0B2530]">{pillar.title}</h4>
                  <p className="mt-3 text-base text-[#617079]">{pillar.copy}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section id="contact" className="mx-auto max-w-6xl px-6 py-12 lg:py-18 scroll-mt-20">
          <div className="rounded-[32px] bg-[#0B2530] px-10 lg:px-16 py-16 lg:py-20 text-white shadow-2xl shadow-[#071136]/40">
            <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
              <div className="max-w-2xl">
                <p className="text-sm font-semibold uppercase tracking-[0.3em] text-[#fffff]">Ready to launch</p>
                <h3 className="mt-4 text-4xl lg:text-5xl font-bold">Exit the chaos. Welcome proactive support.</h3>
                <p className="mt-6 text-base lg:text-lg text-white/70">
                  Empower every team with request automation, curated knowledge, and lightning-fast handoffs.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link className="hidden rounded-2xl bg-white px-8 py-4 text-base font-semibold text-[#0F2A29] hover:bg-gray-100 transition-colors text-center" to="/signin">
                  Sign In
                </Link>
                <Link className="hidden rounded-2xl border border-white/40 px-8 py-4 text-base font-semibold hover:bg-white/10 transition-colors text-center" to="/portal">
                  Explore Portal
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Search Knowledge Base — same Advanced Knowledge Search as portal; public API on sign-in */}
      <Modal
        isOpen={showSearchKBModal}
        onClose={() => setShowSearchKBModal(false)}
        title="Search Knowledge Base"
        size="full"
      >
        <div className="max-h-[85vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          <KnowledgeSearch
            key={kbModalSeedQuery || 'kb-landing-default'}
            initialQuery={kbModalSeedQuery}
            runSearchOnMount={Boolean(kbModalSeedQuery?.trim())}
            usePublicSearch
            publicSearchTenantId={resolveLandingTenantId()}
          />
        </div>
      </Modal>

      {/* Customer Signup Modal */}
      {showCustomerSignupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ boxShadow: '0 4px 12px rgba(7, 17, 54, 0.3)' }}>
                    <img src={logo} alt="Shield" className="h-8 w-8" />
                  </div>
                  <h2 className="text-2xl font-bold" style={{ color: '#1a365d' }}>Create New Account</h2>
                </div>
                <button
                  onClick={() => {
                    setShowCustomerSignupModal(false);
                    setSignupError('');
                    setFieldErrors({ email: '', phone: '' });
                    setSignupEmailReadOnly(true);
                    setSignupFormData({
                      fullName: '',
                      firstName: '',
                      lastName: '',
                      email: '',
                      username: '',
                      phone: '',
                      defaultPassword: '',
                      role: 'CUSTOMER'
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleCustomerSignup} className="space-y-4">
                {signupError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{signupError}</p>
                  </div>
                )}

                {/* Full Name */}
                <div>
                  <label className="block text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: '#4a5568' }}>
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={signupFormData.fullName}
                    onChange={(e) => setSignupFormData({ ...signupFormData, fullName: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border-2 transition-all focus:outline-none focus:border-[#006600]"
                    style={{ borderColor: '#e2e8f0', background: 'rgba(255, 255, 255, 0.9)' }}
                    placeholder="Enter your full name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: '#4a5568' }}>
                    Email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: '#718096' }} />
                    <input
                      id="user_registration_email"
                      name="user_registration_email"
                      type="email"
                      inputMode="email"
                      required
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                      readOnly={signupEmailReadOnly}
                      value={signupFormData.email}
                      onChange={(e) => {
                        setSignupFormData({ ...signupFormData, email: e.target.value });
                        // Clear error when user starts typing
                        if (fieldErrors.email) {
                          setFieldErrors({ ...fieldErrors, email: '' });
                        }
                      }}
                      onFocus={() => setSignupEmailReadOnly(false)}
                      onBlur={(e) => {
                        // Validate on blur
                        const error = validateEmail(e.target.value);
                        setFieldErrors({ ...fieldErrors, email: error });
                      }}
                      className={`w-full pl-12 pr-4 py-3 rounded-xl border-2 transition-all focus:outline-none ${
                        fieldErrors.email ? 'border-red-500 focus:border-red-500' : 'focus:border-[#006600]'
                      }`}
                      style={{ 
                        borderColor: fieldErrors.email ? '#ef4444' : '#e2e8f0', 
                        background: 'rgba(255, 255, 255, 0.9)' 
                      }}
                      placeholder="Enter your email"
                    />
                  </div>
                  {fieldErrors.email && (
                    <p className="mt-1 text-xs text-red-600">{fieldErrors.email}</p>
                  )}
                </div>

                {/* Mobile Number */}
                <div>
                  <label className="block text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: '#4a5568' }}>
                    Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    required
                    value={signupFormData.phone}
                    onChange={(e) => {
                      // Only allow digits and limit to 10 characters
                      const inputValue = e.target.value.replace(/\D/g, ''); // Remove non-digits
                      const limitedValue = inputValue.slice(0, 10); // Limit to 10 digits
                      setSignupFormData({ ...signupFormData, phone: limitedValue });
                      // Clear error when user starts typing
                      if (fieldErrors.phone) {
                        setFieldErrors({ ...fieldErrors, phone: '' });
                      }
                      // Real-time validation feedback
                      if (limitedValue && limitedValue.length < 9) {
                        setFieldErrors({ ...fieldErrors, phone: '' }); // Clear error while typing
                      } else if (limitedValue && limitedValue.length >= 9) {
                        const error = validatePhone(limitedValue);
                        setFieldErrors({ ...fieldErrors, phone: error });
                      }
                    }}
                    onBlur={(e) => {
                      // Validate on blur
                      const error = validatePhone(e.target.value);
                      setFieldErrors({ ...fieldErrors, phone: error });
                    }}
                    maxLength={10}
                    className={`w-full px-4 py-3 rounded-xl border-2 transition-all focus:outline-none ${
                      fieldErrors.phone ? 'border-red-500 focus:border-red-500' : 'focus:border-[#006600]'
                    }`}
                    style={{ 
                      borderColor: fieldErrors.phone ? '#ef4444' : '#e2e8f0', 
                      background: 'rgba(255, 255, 255, 0.9)' 
                    }}
                    placeholder="Enter your mobile number (9 or 10 digits)"
                  />
                  {fieldErrors.phone && (
                    <p className="mt-1 text-xs text-red-600">{fieldErrors.phone}</p>
                  )}
                  {signupFormData.phone && signupFormData.phone.length > 0 && signupFormData.phone.length < 9 && (
                    <p className="mt-1 text-xs text-gray-500">{signupFormData.phone.length}/9-10 digits</p>
                  )}
                </div>

                {/* Default Password */}
                <div>
                  <label className="block text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: '#4a5568' }}>
                    Default Password <span className="text-red-500">*</span>
                  </label>
                  <DefaultPasswordRequirementsPopover password={signupFormData.defaultPassword}>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: '#718096' }} />
                      <input
                        id="user_registration_password"
                        name="user_registration_new_password"
                        type={showSignupPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        value={signupFormData.defaultPassword}
                        onChange={(e) => setSignupFormData({ ...signupFormData, defaultPassword: e.target.value })}
                        className="w-full pl-12 pr-12 py-3 rounded-xl border-2 transition-all focus:outline-none focus:border-[#006600]"
                        style={{
                          borderColor:
                            signupFormData.defaultPassword && customerSignupPasswordInvalid
                              ? '#ef4444'
                              : '#e2e8f0',
                          background: 'rgba(255, 255, 255, 0.9)',
                        }}
                        placeholder="Enter default password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSignupPassword(!showSignupPassword)}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 transition-colors"
                        style={{ color: '#718096' }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = '#071136'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = '#718096'; }}
                        aria-label={showSignupPassword ? 'Hide password' : 'Show password'}
                      >
                        {showSignupPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </DefaultPasswordRequirementsPopover>
                </div>

                {/* Role (Disabled) */}
                <div>
                  <label className="block text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: '#4a5568' }}>
                    Role
                  </label>
                  <input
                    type="text"
                    value="CUSTOMER"
                    disabled
                    className="w-full px-4 py-3 rounded-xl border-2 bg-gray-100 cursor-not-allowed"
                    style={{ borderColor: '#e2e8f0', color: '#718096' }}
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={customerSignupSubmitDisabled}
                  className="w-full py-4 rounded-xl font-bold text-white text-base transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  style={{
                    background: customerSignupSubmitDisabled
                      ? 'linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)'
                      : 'linear-gradient(135deg,#006600 0%,#BB0000 100%)',
                    boxShadow: customerSignupSubmitDisabled
                      ? '0 4px 12px rgba(156, 163, 175, 0.3)'
                      : '0 4px 20px rgba(7, 17, 54, 0.4)'
                  }}
                >
                  {signupLoading ? 'CREATING ACCOUNT...' : 'SAVE'}
                </button>
              </form>
              <div className="text-center pt-2 mb-4">
                <label className="text-gray-600">(OR)</label>
              </div>
              
              {/* Social Login Icons - Single Row */}
              <div className="flex items-center justify-center gap-6">
                <button
                  type="button"
                  onClick={handleGoogleOAuthSignupLogin}
                  className="flex items-center justify-center p-2 rounded-full hover:bg-gray-100 transition-colors"
                  title="Login with Google"
                  aria-label="Login with Google"
                >
                  <FcGoogle 
                    size={40} 
                    style={{ cursor: "pointer" }}
                  />
                </button>

                <button
                  type="button"
                  onClick={() => {
                    window.open('https://www.linkedin.com/', '_blank', 'noopener,noreferrer');
                  }}
                  className="flex items-center justify-center p-2 rounded-full hover:bg-gray-100 transition-colors"
                  title="Login with LinkedIn"
                  aria-label="Login with LinkedIn"
                >
                  <FaLinkedin 
                    size={40} 
                    color="#0A66C2"
                    style={{ cursor: "pointer" }}
                  />
                </button>

                <button
                  type="button"
                  onClick={() => {
                    window.open('https://www.facebook.com/', '_blank', 'noopener,noreferrer');
                  }}
                  className="flex items-center justify-center p-2 rounded-full hover:bg-gray-100 transition-colors"
                  title="Login with Facebook"
                  aria-label="Login with Facebook"
                >
                  <FaFacebook 
                    size={40} 
                    color="#1877F2"
                    style={{ cursor: "pointer" }}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Google Account Consent Modal */}
      {showGoogleConsentModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#F8FCFB]">
                  <FcGoogle size={24} />
                </div>
                <h3 className="text-xl font-bold" style={{ color: '#1a365d' }}>Create Account</h3>
              </div>
              <p className="text-sm leading-6" style={{ color: '#4a5568' }}>
                No HelpDesk account was found for this Google email. Do you want to create a new account now?
              </p>
              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCancelGoogleAccountCreation}
                  className="px-5 py-2.5 rounded-xl font-semibold border transition-colors"
                  style={{ borderColor: '#d1d5db', color: '#374151', background: '#fff' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmGoogleAccountCreation}
                  className="px-5 py-2.5 rounded-xl font-semibold text-white transition-colors"
                  style={{ background: 'linear-gradient(135deg,#006600 0%,#BB0000 100%)' }}
                >
                  Create Account
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-[#E5EFF3] bg-white/80">
        <div className="mx-auto max-w-6xl px-6 py-10 text-sm text-[#6C7A82]">
          <div
            className={`grid transition-all duration-300 ease-out ${
              showContactBar ? 'mb-4 grid-rows-[1fr] opacity-100' : 'mb-0 grid-rows-[0fr] opacity-0'
            }`}
            aria-hidden={!showContactBar}
          >
            <div className="overflow-hidden">
              <div className="mx-auto flex w-full max-w-2xl flex-col items-center justify-center gap-3 rounded-xl border border-[#E5EFF3] bg-white px-4 py-2.5 shadow-sm sm:flex-row sm:gap-6">
                <div className="inline-flex items-center gap-2 text-sm text-[#4A5B63]">
                  <Phone className="h-4 w-4 shrink-0 text-[#006600]" aria-hidden="true" />
                  <span>Contact Support : 040 - 49583232</span>
                </div>
                <div className="inline-flex items-center gap-2 text-sm text-[#4A5B63]">
                  <Mail className="h-4 w-4 shrink-0 text-[#006600]" aria-hidden="true" />
                  <span>Email Support : helpdesk@dhspltd.com</span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 items-center gap-4 sm:grid-cols-3">
            <div className="flex items-center justify-center gap-2 text-base font-semibold text-[#0B2530] sm:justify-start">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl">
                <img
                  src={logo}
                  alt="Shield"
                  className="h-10 w-10 object-contain"
                />
              </div>
              {tenantName}
            </div>
            <p className="text-center">© {new Date().getFullYear()} Helpdesk. All rights reserved.</p>
            <div className="flex flex-wrap items-center justify-center gap-4 sm:justify-end">
              <button
                type="button"
                onClick={() => setShowContactBar((open) => !open)}
                className="font-semibold transition-colors hover:text-[#006600]"
                style={{ color: '#0B2530' }}
                aria-expanded={showContactBar}
              >
                Contact Us
              </button>
              <Link
                to="/privacy"
                className="font-semibold transition-colors hover:text-[#006600]"
                style={{ color: '#0B2530' }}
              >
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Chatbot Widget */}
      <LandingChatbot />

      {/* Forgot Password Modal */}
      {showForgotPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold" style={{ color: '#071136' }}>Forgot Password</h2>
              <button
                onClick={() => {
                  setShowForgotPasswordModal(false);
                  setForgotPasswordEmail('');
                  setForgotPasswordError('');
                  setForgotPasswordSuccess(false);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {!forgotPasswordSuccess ? (
              <>
                <p className="text-gray-600 mb-6">
                  Enter your email address and we'll send you a password reset token.
                </p>
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    setForgotPasswordError('');
                    setForgotPasswordLoading(true);

                    try {
                      await authService.forgotPassword(forgotPasswordEmail);
                      setForgotPasswordSuccess(true);
                      // Store timestamp when token was generated (expires in 5 minutes)
                      setResetTokenGeneratedAt(new Date().getTime());
                      setResetTokenCountdown(300); // Reset to 5 minutes
                      toast.success('Password reset token sent to your email');
                    } catch (error) {
                      const errorMsg = extractErrorMessage(error);
                      setForgotPasswordError(errorMsg);
                      toast.error(errorMsg);
                    } finally {
                      setForgotPasswordLoading(false);
                    }
                  }}
                >
                  <div className="mb-4">
                    <label className="block text-sm font-semibold mb-2" style={{ color: '#4a5568' }}>
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      required
                      className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:border-[#006600]"
                      placeholder="Enter your email"
                    />
                  </div>

                  {forgotPasswordError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      {forgotPasswordError}
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPasswordModal(false);
                        setForgotPasswordEmail('');
                        setForgotPasswordError('');
                        setForgotPasswordSuccess(false);
                      }}
                      className="flex-1 py-3 rounded-xl font-semibold border-2 transition-colors"
                      style={{ borderColor: '#cbd5e0', color: '#4a5568' }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={forgotPasswordLoading || !forgotPasswordEmail}
                      className="flex-1 py-3 rounded-xl font-bold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        background: forgotPasswordLoading || !forgotPasswordEmail
                          ? 'linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)'
                          : 'linear-gradient(135deg,#006600 0%,#BB0000 100%)'
                      }}
                    >
                      {forgotPasswordLoading ? 'Sending...' : 'Send Token'}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div>
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-700 text-sm">
                    A password reset token has been sent to <strong>{forgotPasswordEmail}</strong>.
                    Please check your email and enter the token below to reset your password.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowForgotPasswordModal(false);
                    setShowResetPasswordModal(true);
                    setResetPasswordEmail(forgotPasswordEmail);
                    // Calculate remaining time if token was already generated
                    if (resetTokenGeneratedAt) {
                      const now = new Date().getTime();
                      const elapsed = Math.floor((now - resetTokenGeneratedAt) / 1000);
                      const remaining = Math.max(0, 300 - elapsed);
                      setResetTokenCountdown(remaining);
                    } else {
                      // If no timestamp, assume it was just generated
                      setResetTokenGeneratedAt(new Date().getTime());
                      setResetTokenCountdown(300);
                    }
                    setForgotPasswordEmail('');
                    setForgotPasswordError('');
                    setForgotPasswordSuccess(false);
                  }}
                  className="w-full py-3 rounded-xl font-bold text-white transition-all"
                  style={{ background: 'linear-gradient(135deg,#006600 0%,#BB0000 100%)' }}
                >
                  Enter Reset Token
                </button>
                <button
                  onClick={() => {
                    setShowForgotPasswordModal(false);
                    setForgotPasswordEmail('');
                    setForgotPasswordError('');
                    setForgotPasswordSuccess(false);
                  }}
                  className="w-full mt-3 py-3 rounded-xl font-semibold border-2 transition-colors"
                  style={{ borderColor: '#cbd5e0', color: '#4a5568' }}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold" style={{ color: '#071136' }}>Reset Password</h2>
              <button
                onClick={() => {
                  setShowResetPasswordModal(false);
                  setResetPasswordEmail('');
                  setResetToken('');
                  setResetNewPassword('');
                  setResetConfirmPassword('');
                  setResetPasswordError('');
                  setShowResetNewPassword(false);
                  setShowResetConfirmPassword(false);
                  setResetTokenGeneratedAt(null);
                  setResetTokenCountdown(300);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setResetPasswordError('');

                if (resetNewPassword !== resetConfirmPassword) {
                  setResetPasswordError('Passwords do not match');
                  return;
                }

                if (resetNewPassword.length < 8) {
                  setResetPasswordError('Password must be at least 8 characters');
                  return;
                }

                setResetPasswordLoading(true);

                try {
                  await authService.resetPassword(
                    resetPasswordEmail,
                    resetToken,
                    resetNewPassword,
                    resetConfirmPassword
                  );
                  toast.success('Password reset successfully! You can now login with your new password.');
                  setShowResetPasswordModal(false);
                  setResetPasswordEmail('');
                  setResetToken('');
                  setResetNewPassword('');
                  setResetConfirmPassword('');
                  setResetPasswordError('');
                  setShowResetNewPassword(false);
                  setShowResetConfirmPassword(false);
                  setResetTokenGeneratedAt(null);
                  setResetTokenCountdown(300);
                } catch (error) {
                  const errorMsg = extractErrorMessage(error);
                  setResetPasswordError(errorMsg);
                  toast.error(errorMsg);
                } finally {
                  setResetPasswordLoading(false);
                }
              }}
            >
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2" style={{ color: '#4a5568' }}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={resetPasswordEmail}
                  onChange={(e) => setResetPasswordEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:border-[#006600]"
                  placeholder="Enter your email"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2" style={{ color: '#4a5568' }}>
                  Reset Token
                </label>
                <input
                  type="text"
                  value={resetToken}
                  onChange={(e) => {
                    // Only allow numeric digits (0-9), remove all other characters
                    const numericValue = e.target.value.replace(/[^0-9]/g, '');
                    // Limit to 6 digits
                    const limitedValue = numericValue.slice(0, 6);
                    setResetToken(limitedValue);
                  }}
                  onKeyDown={(e) => {
                    // Prevent non-numeric keys (except backspace, delete, tab, arrow keys)
                    const allowedKeys = [
                      'Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 
                      'ArrowUp', 'ArrowDown', 'Home', 'End'
                    ];
                    const isNumeric = /^[0-9]$/.test(e.key);
                    if (!isNumeric && !allowedKeys.includes(e.key) && !e.ctrlKey && !e.metaKey) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    // Handle paste event - only allow numeric characters
                    e.preventDefault();
                    const pastedText = e.clipboardData.getData('text');
                    const numericValue = pastedText.replace(/[^0-9]/g, '');
                    const limitedValue = numericValue.slice(0, 6);
                    setResetToken(limitedValue);
                  }}
                  required
                  className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:border-[#006600]"
                  placeholder="Enter 6-digit token from email"
                  maxLength={6}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  disabled={resetTokenCountdown === 0}
                />
                {/* Countdown Timer */}
                <div className="flex items-center gap-2 mt-2 text-sm font-medium" 
                     style={{ color: resetTokenCountdown < 60 ? '#BB0000' : resetTokenCountdown < 120 ? '#FF6B00' : '#4A5B63' }}>
                  <Clock className="w-4 h-4" />
                  <span>
                    {resetTokenCountdown > 0 
                      ? `Token expires in ${Math.floor(resetTokenCountdown / 60)}:${String(resetTokenCountdown % 60).padStart(2, '0')}`
                      : 'Token has expired. Please request a new one.'
                    }
                  </span>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2" style={{ color: '#4a5568' }}>
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showResetNewPassword ? 'text' : 'password'}
                    value={resetNewPassword}
                    onChange={(e) => setResetNewPassword(e.target.value)}
                    required
                    minLength={8}
                    className="w-full px-4 py-3 pr-12 border-2 rounded-xl focus:outline-none focus:border-[#006600]"
                    placeholder="Enter new password (min 8 characters)"
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetNewPassword(!showResetNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                    style={{ color: '#718096' }}
                    onMouseEnter={(e) => e.target.style.color = '#071136'}
                    onMouseLeave={(e) => e.target.style.color = '#718096'}
                  >
                    {showResetNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2" style={{ color: '#4a5568' }}>
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showResetConfirmPassword ? 'text' : 'password'}
                    value={resetConfirmPassword}
                    onChange={(e) => setResetConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    className="w-full px-4 py-3 pr-12 border-2 rounded-xl focus:outline-none focus:border-[#006600]"
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetConfirmPassword(!showResetConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                    style={{ color: '#718096' }}
                    onMouseEnter={(e) => e.target.style.color = '#071136'}
                    onMouseLeave={(e) => e.target.style.color = '#718096'}
                  >
                    {showResetConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {resetPasswordError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {resetPasswordError}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowResetPasswordModal(false);
                    setResetPasswordEmail('');
                    setResetToken('');
                    setResetNewPassword('');
                    setResetConfirmPassword('');
                    setResetPasswordError('');
                    setShowResetNewPassword(false);
                    setShowResetConfirmPassword(false);
                  }}
                  className="flex-1 py-3 rounded-xl font-semibold border-2 transition-colors"
                  style={{ borderColor: '#cbd5e0', color: '#4a5568' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={resetPasswordLoading || !resetPasswordEmail || !resetToken || !resetNewPassword || !resetConfirmPassword || resetTokenCountdown === 0}
                  className="flex-1 py-3 rounded-xl font-bold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: resetPasswordLoading || !resetPasswordEmail || !resetToken || !resetNewPassword || !resetConfirmPassword || resetTokenCountdown === 0
                      ? 'linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)'
                      : 'linear-gradient(135deg,#006600 0%,#BB0000 100%)'
                  }}
                >
                  {resetPasswordLoading ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ActiveSessionLoginPrompt dialog={activeSessionPrompt} />
    </div>
  );
};

export default LandingPage;
